<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { isImageAttachment, isPdfAttachment } from '@satellite/helpers/attachmentUtils';

import type { YardTaskAttachment } from '@nova/core';

const PDF_FILE_ICON = 'mdi-file-pdf-box';
const FALLBACK_FILE_ICON = 'mdi-file-outline';

defineOptions({ name: 'AttachmentThumbnail' });

const {
  attachment,
  href = '',
  selectable = true,
  removable = false,
  isRemoving = false,
  removeLabel = '',
  testId = 'attachment'
} = defineProps<{
  /** The document this tile stands for; its mimetype decides what is rendered. */
  attachment: YardTaskAttachment;
  /**
   * Renders the tile as a link to this URL instead of a button, for what the app has no preview
   * of its own for. Without it the tile only reports the click through `select`.
   */
  href?: string;
  /**
   * Whether the tile opens its attachment at all. Off, it still shows the document but is
   * inert — for callers that may display attachments without being allowed to open them.
   */
  selectable?: boolean;
  /** Renders the remove affordance; the tile only reports it through `remove`. */
  removable?: boolean;
  /** Blocks the remove affordance while a removal is already in flight. */
  isRemoving?: boolean;
  /** Accessible name for the remove affordance. Falls back to a plain "Remove". */
  removeLabel?: string;
  /** Prefix for every `data-testid` this tile renders, e.g. `<testId>-row`. */
  testId?: string;
}>();

const emit = defineEmits<{
  select: [];
  remove: [];
  'error:thumbnail': [];
}>();

const { t } = useI18n();

const isImage = computed(() => isImageAttachment(attachment));

// Never empty: this is the only name a screen reader has for the sole destructive control.
const removeAccessibleLabel = computed(() => removeLabel || t('global.basicTerms.remove'));

const fileIcon = computed(() => (isPdfAttachment(attachment) ? PDF_FILE_ICON : FALLBACK_FILE_ICON));

/**
 * The tile is a link to whatever the app cannot preview itself, and a button otherwise —
 * element, attributes and click behaviour all decided together, so the two forms cannot drift.
 */
const tile = computed(() => {
  if (!selectable) {
    return { tag: 'div', attrs: {} };
  }

  return href
    ? { tag: 'a', attrs: { href, target: '_blank', rel: 'noopener noreferrer' } }
    : { tag: 'button', attrs: { type: 'button', onClick: () => emit('select') } };
});
</script>

<template>
  <div class="attachment-thumbnail">
    <component
      :is="tile.tag"
      v-bind="tile.attrs"
      class="attachment-thumbnail__tile text-decoration-none"
      :title="attachment.name"
      :data-testid="`${testId}-row`">
      <span class="attachment-thumbnail__preview d-flex align-center justify-center">
        <v-img
          v-if="isImage"
          :src="attachment.url"
          :alt="attachment.name"
          cover
          class="attachment-thumbnail__image"
          :data-testid="`${testId}-thumbnail`"
          @error="emit('error:thumbnail')">
          <template #error>
            <v-icon :icon="FALLBACK_FILE_ICON" size="24" class="text--color-text-secondary" />
          </template>
        </v-img>

        <v-icon v-else :icon="fileIcon" size="24" class="text--color-text-secondary" />
      </span>

      <span class="attachment-thumbnail__name typography-caption text--color-text-secondary">
        {{ attachment.name }}
      </span>
    </component>

    <button
      v-if="removable"
      type="button"
      class="attachment-thumbnail__remove d-flex align-center justify-center"
      :disabled="isRemoving"
      :aria-label="removeAccessibleLabel"
      :data-testid="`${testId}-remove-button`"
      @click="emit('remove')">
      <v-icon icon="mdi-close" size="14" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
.attachment-thumbnail {
  /* Overridable by the consumer: a drawer has room a phone does not. */
  --attachment-thumbnail-size: 72px;

  position: relative;
}

.attachment-thumbnail__tile {
  /**
   * Rows rather than a flex column so neither row is sized by what it holds: `minmax(0, 1fr)`
   * gives the preview the leftovers and nothing more, and the name keeps its single line.
   * VImg would otherwise dictate the split, since it sizes itself to the image's aspect ratio
   * and refuses to shrink, pushing the name out of the tile for anything tall.
   */
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  width: var(--attachment-thumbnail-size);
  height: var(--attachment-thumbnail-size);
  overflow: hidden;
  border: 1px solid $color-line-divider;
  border-radius: $default-border-radius;
  background-color: $color-neutral-0;
  color: inherit;
}

.attachment-thumbnail__preview {
  width: 100%;
  overflow: hidden;
  border-bottom: 1px solid $color-line-divider;
  background-color: $color-background-transparency-inverted;
}

.attachment-thumbnail__image {
  width: 100%;
  height: 100%;

  :deep(.v-img__error) {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
  }
}

.attachment-thumbnail__name {
  width: 100%;
  padding: $spacing-0-5 $spacing-1;
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-thumbnail__remove {
  position: absolute;
  z-index: 1;
  top: -6px;
  right: -6px;
  width: $spacing-5;
  height: $spacing-5;
  padding: 0;
  border: 1px solid $color-line-border;
  border-radius: 50%;
  background-color: $color-neutral-0;
  color: $color-text-secondary;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}
</style>
